import { ReactNode } from 'react'

type TPlainObject = Record<string, unknown>

const isPlainObject = (value: unknown): value is TPlainObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isPrimitive = (value: unknown) => typeof value !== 'object' || value === null

const isEmpty = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value).length === 0)

const EmptyValue = () => <span className="c-text-gray-400">—</span>

function Primitive({ value }: Readonly<{ value: unknown }>) {
  if (isEmpty(value)) return <EmptyValue />
  return <span className="c-font-mono c-text-sm c-break-words">{String(value)}</span>
}

/** Deepest levels are not worth a dedicated layout: show them as compact JSON */
function InlineValue({ value }: Readonly<{ value: unknown }>) {
  if (isPrimitive(value) || isEmpty(value)) return <Primitive value={value} />
  return <span className="c-font-mono c-text-sm c-break-all">{JSON.stringify(value)}</span>
}

function KeyValueGrid({ entries }: Readonly<{ entries: TPlainObject }>) {
  return (
    <dl className="c-m-0 c-grid c-grid-cols-[auto_1fr] c-gap-x-3 c-gap-y-1">
      {Object.entries(entries).map(([key, entryValue]) => (
        <div key={key} className="c-contents">
          <dt className="c-text-xs c-font-medium c-text-gray-500 c-break-words">{key}</dt>
          <dd className="c-m-0 c-min-w-0">
            <InlineValue value={entryValue} />
          </dd>
        </div>
      ))}
    </dl>
  )
}

function Card({ children }: Readonly<{ children: ReactNode }>) {
  return <div className="c-rounded-lg c-border c-border-gray-200 c-bg-white c-px-2 c-py-1.5">{children}</div>
}

/**
 * Renders an interop field value: primitives as text, objects as a key/value grid,
 * arrays as chips (primitives) or one card per item (objects). Anything nested deeper
 * falls back to compact JSON.
 */
export function InteropValueComponent({ value }: Readonly<{ value: unknown }>) {
  if (isEmpty(value) || isPrimitive(value)) return <Primitive value={value} />

  if (Array.isArray(value)) {
    if (value.every(isPrimitive)) {
      return (
        <ul className="c-m-0 c-flex c-list-none c-flex-wrap c-gap-1 c-p-0">
          {value.map((item, itemIndex) => (
            <li
              key={`${itemIndex}-${String(item)}`}
              className="c-rounded-full c-bg-gray-100 c-px-2 c-py-0.5 c-text-gray-700"
            >
              <Primitive value={item} />
            </li>
          ))}
        </ul>
      )
    }
    return (
      <ul className="c-m-0 c-flex c-list-none c-flex-col c-gap-1.5 c-p-0">
        {value.map((item, itemIndex) => (
          <li key={itemIndex}>
            <Card>
              {isPlainObject(item) ? <KeyValueGrid entries={item} /> : <InlineValue value={item} />}
            </Card>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <Card>
      <KeyValueGrid entries={value as TPlainObject} />
    </Card>
  )
}
